
import React from 'react';
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, AlertCircle, Clock, Lightbulb } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface BioProgressIndicatorProps {
  bioLength: number;
  minLength: number;
  isAiValidating: boolean;
  isAiValid: boolean;
  hasAiValidated: boolean;
}

export const BioProgressIndicator: React.FC<BioProgressIndicatorProps> = ({
  bioLength,
  minLength = 50,
  isAiValidating,
  isAiValid,
  hasAiValidated
}) => {
  const getStage = () => {
    if (bioLength === 0) return 'empty';
    if (bioLength < minLength) return 'tooShort';
    if (isAiValidating) return 'validating';
    if (hasAiValidated && isAiValid) return 'valid';
    if (hasAiValidated && !isAiValid) return 'invalid';
    return 'ready';
  };

  const stage = getStage();
  const charactersNeeded = Math.max(0, minLength - bioLength);

  const getProgressValue = () => {
    if (stage === 'empty') return 0;
    if (stage === 'tooShort') return Math.min((bioLength / minLength) * 70, 70);
    if (stage === 'validating') return 100;
    if (stage === 'valid') return 100;
    if (stage === 'invalid') return 100;
    if (bioLength >= minLength) return 100;
    return 70;
  };

  const getStageInfo = () => {
    switch (stage) {
      case 'empty':
        return {
          icon: <Lightbulb className="h-4 w-4 text-blue-400" />,
          message: 'Comece a escrever sua biografia',
          color: 'text-blue-400',
          bgColor: 'bg-blue-500'
        };
      case 'tooShort':
        return {
          icon: <AlertCircle className="h-4 w-4 text-orange-400" />,
          message: charactersNeeded === 1 
            ? `Escreva mais ${charactersNeeded} caractere para continuar`
            : `Escreva mais ${charactersNeeded} caracteres para continuar`,
          color: 'text-orange-400',
          bgColor: 'bg-orange-500'
        };
      case 'validating':
        return {
          icon: <Clock className="h-4 w-4 text-blue-400 animate-spin" />,
          message: 'IA analisando sua biografia...',
          color: 'text-blue-400',
          bgColor: 'bg-blue-500'
        };
      case 'valid':
        return {
          icon: <CheckCircle2 className="h-4 w-4 text-green-400" />,
          message: 'Biografia aprovada pela IA!',
          color: 'text-green-400',
          bgColor: 'bg-green-500'
        };
      case 'invalid':
        return {
          icon: <AlertCircle className="h-4 w-4 text-red-400" />,
          message: 'IA sugere melhorias na biografia',
          color: 'text-red-400',
          bgColor: 'bg-red-500'
        };
      default:
        return {
          icon: <Clock className="h-4 w-4 text-blue-400" />,
          message: 'Pronto para validação',
          color: 'text-blue-400',
          bgColor: 'bg-blue-500'
        };
    }
  };

  const stageInfo = getStageInfo();
  const progressValue = getProgressValue();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {stageInfo.icon}
          <span className={`text-xs font-medium ${stageInfo.color}`}>
            {stageInfo.message}
          </span>
        </div>
        <span className="text-xs text-white/50">
          {bioLength}/500
        </span>
      </div>
      
      <Progress 
        value={progressValue} 
        className="h-2 bg-white/10" 
        indicatorClassName={stageInfo.bgColor}
      />
    </div>
  );
};
